import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { ArrowRight, ArrowLeft, CheckCircle, SealCheck, Leaf, Users, Globe, Shield, Factory, Diamond, House } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Apparel', 'Jewelry', 'Homeware', 'Accessories', 'Bags', 'Footwear', 'Textiles', 'Art'];

const TEMPLATE_ICONS = {
  textiles_apparel: Factory,
  jewelry_accessories: Diamond,
  leather_bags: Shield,
  home_furniture: House,
  general: Globe
};

const CertifyPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Template & Product Info, 2: Driver Questions, 3: Review, 4: Certificate
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [previewScore, setPreviewScore] = useState(null);
  
  const [productData, setProductData] = useState({
    product_name: '',
    product_description: '',
    product_category: '',
    product_image_url: '',
    artisan_name: '',
    artisan_location: '',
    material: '',
    material_origin: '',
    craftsmanship_method: '',
    industry_template: 'general'
  });
  
  const [driverResponses, setDriverResponses] = useState({});

  // Check if user has iris identity
  const irisId = localStorage.getItem('ginsgood_iris_id');
  const irisName = localStorage.getItem('ginsgood_iris_name');

  useEffect(() => {
    if (!irisId) {
      toast.error('Please complete iris authentication first');
      navigate('/iris-auth');
      return;
    }
    fetchTemplates();
  }, [irisId, navigate]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load industry templates');
    }
  };

  const fetchDriversForTemplate = async (templateId) => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}/drivers`);
      setDrivers(response.data.drivers || []);
      setSelectedTemplate(response.data);
      setDriverResponses({}); // Reset responses when template changes
      setPreviewScore(null);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast.error('Failed to load certification drivers');
    }
  };

  const handleTemplateSelect = (templateId) => {
    setProductData({ ...productData, industry_template: templateId });
    fetchDriversForTemplate(templateId);
  };

  const calculatePreviewScore = async () => {
    const responses = Object.entries(driverResponses).map(([driver_id, selected_option]) => ({
      driver_id,
      selected_option
    }));
    
    if (responses.length === 0) {
      setPreviewScore(null);
      return;
    }

    try {
      // Calculate score locally based on driver weights
      let totalScore = 0;
      let maxPossible = 0;
      const categoryScores = { authenticity: 0, heritage: 0, social_impact: 0, sustainability: 0 };
      const categoryMax = { authenticity: 0, heritage: 0, social_impact: 0, sustainability: 0 };

      for (const driver of drivers) {
        const response = driverResponses[driver.id];
        if (response) {
          // Find the score for this response (we'll estimate based on option position)
          const optionIndex = driver.options.indexOf(response);
          const score = Math.max(0, driver.max_score - (optionIndex * (driver.max_score / driver.options.length)));
          totalScore += score;
          categoryScores[driver.category] += score;
        }
        maxPossible += driver.max_score;
        categoryMax[driver.category] += driver.max_score;
      }

      const rawPercentage = maxPossible > 0 ? (totalScore / maxPossible) * 100 : 0;
      const finalScore = Math.min(rawPercentage, 90); // Cap at 90%

      const categoryPercentages = {};
      for (const cat of Object.keys(categoryScores)) {
        if (categoryMax[cat] > 0) {
          categoryPercentages[cat] = Math.min((categoryScores[cat] / categoryMax[cat]) * 100, 90);
        } else {
          categoryPercentages[cat] = 0;
        }
      }

      setPreviewScore({
        ginsgood_score: Math.round(finalScore * 10) / 10,
        category_percentages: Object.fromEntries(
          Object.entries(categoryPercentages).map(([k, v]) => [k, Math.round(v * 10) / 10])
        ),
        score_cap_applied: rawPercentage > 90
      });
    } catch (error) {
      console.error('Error calculating score:', error);
    }
  };

  useEffect(() => {
    if (step === 2 && drivers.length > 0) {
      calculatePreviewScore();
    }
  }, [driverResponses, step, drivers]);

  const handleDriverChange = (driverId, value) => {
    setDriverResponses(prev => ({ ...prev, [driverId]: value }));
  };

  const handleSubmitCertification = async () => {
    setLoading(true);
    try {
      const responses = Object.entries(driverResponses).map(([driver_id, selected_option]) => ({
        driver_id,
        selected_option
      }));

      const payload = {
        ...productData,
        seller_iris_id: irisId,
        driver_responses: responses
      };

      const response = await axios.post(`${API}/certify`, payload);
      
      if (response.data.success) {
        setCertificate(response.data);
        setStep(4);
        toast.success('Product certified successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to certify product');
    } finally {
      setLoading(false);
    }
  };

  const progress = (step / 4) * 100;
  const answeredDrivers = Object.keys(driverResponses).length;
  const totalDrivers = drivers.length;

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'authenticity': return <Shield weight="duotone" className="w-5 h-5" />;
      case 'heritage': return <Globe weight="duotone" className="w-5 h-5" />;
      case 'social_impact': return <Users weight="duotone" className="w-5 h-5" />;
      case 'sustainability': return <Leaf weight="duotone" className="w-5 h-5" />;
      default: return null;
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'authenticity': return '#2C3E2D';
      case 'heritage': return '#C26D53';
      case 'social_impact': return '#D4A373';
      case 'sustainability': return '#4A6C4C';
      default: return '#6B6659';
    }
  };

  const getTierInfo = (score) => {
    if (score >= 85) return { name: 'Platinum', color: '#E5E4E2', icon: '💎' };
    if (score >= 70) return { name: 'Gold', color: '#FFD700', icon: '🥇' };
    if (score >= 55) return { name: 'Silver', color: '#C0C0C0', icon: '🥈' };
    if (score >= 40) return { name: 'Bronze', color: '#CD7F32', icon: '🥉' };
    return { name: 'Uncertified', color: '#999999', icon: '⚪' };
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge variant="outline" className="mb-4">
              <SealCheck weight="duotone" className="w-4 h-4 mr-2" />
              Product Certification
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Certify Your Product
            </h1>
            <p className="text-muted-foreground">
              Verified by: <span className="text-primary font-medium">{irisName}</span>
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Step {step} of 4</span>
              <span className="text-muted-foreground">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Step 1: Template & Product Information */}
          {step === 1 && (
            <Card className="p-8" data-testid="step-product-info">
              <h2 className="text-xl font-medium mb-6">Select Industry & Product Information</h2>
              
              {/* Industry Template Selection */}
              <div className="mb-8">
                <Label className="text-base mb-4 block">Select Industry Template *</Label>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map(template => {
                    const Icon = TEMPLATE_ICONS[template.id] || Globe;
                    const isSelected = productData.industry_template === template.id;
                    return (
                      <button
                        key={template.id}
                        onClick={() => handleTemplateSelect(template.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                          isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`template-${template.id}`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                            <Icon weight="duotone" className="w-5 h-5" />
                          </div>
                          {isSelected && <CheckCircle weight="fill" className="w-5 h-5 text-primary ml-auto" />}
                        </div>
                        <h3 className="font-medium">{template.name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">{template.driver_count} drivers</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Product Name *</Label>
                    <Input
                      value={productData.product_name}
                      onChange={(e) => setProductData({ ...productData, product_name: e.target.value })}
                      placeholder="Handwoven Banarasi Silk Stole"
                      required
                      data-testid="product-name"
                    />
                  </div>
                  <div>
                    <Label>Category *</Label>
                    <Select 
                      value={productData.product_category} 
                      onValueChange={(val) => setProductData({ ...productData, product_category: val })}
                    >
                      <SelectTrigger data-testid="product-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={productData.product_description}
                    onChange={(e) => setProductData({ ...productData, product_description: e.target.value })}
                    placeholder="Describe your product, its features, and unique qualities..."
                    rows={4}
                    data-testid="product-description"
                  />
                </div>

                <div>
                  <Label>Product Image URL</Label>
                  <Input
                    value={productData.product_image_url}
                    onChange={(e) => setProductData({ ...productData, product_image_url: e.target.value })}
                    placeholder="https://example.com/product-image.jpg"
                    data-testid="product-image"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Artisan Name *</Label>
                    <Input
                      value={productData.artisan_name}
                      onChange={(e) => setProductData({ ...productData, artisan_name: e.target.value })}
                      placeholder="Craftsman name"
                      data-testid="artisan-name"
                    />
                  </div>
                  <div>
                    <Label>Artisan Location *</Label>
                    <Input
                      value={productData.artisan_location}
                      onChange={(e) => setProductData({ ...productData, artisan_location: e.target.value })}
                      placeholder="Varanasi, India"
                      data-testid="artisan-location"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <Label>Material *</Label>
                    <Input
                      value={productData.material}
                      onChange={(e) => setProductData({ ...productData, material: e.target.value })}
                      placeholder="100% Pure Silk"
                      data-testid="material"
                    />
                  </div>
                  <div>
                    <Label>Material Origin *</Label>
                    <Input
                      value={productData.material_origin}
                      onChange={(e) => setProductData({ ...productData, material_origin: e.target.value })}
                      placeholder="Varanasi"
                      data-testid="material-origin"
                    />
                  </div>
                  <div>
                    <Label>Craftsmanship Method *</Label>
                    <Input
                      value={productData.craftsmanship_method}
                      onChange={(e) => setProductData({ ...productData, craftsmanship_method: e.target.value })}
                      placeholder="Handloom Weaving"
                      data-testid="craftsmanship-method"
                    />
                  </div>
                </div>

                <Button 
                  onClick={() => setStep(2)} 
                  className="w-full h-12 rounded-full gap-2"
                  disabled={!productData.product_name || !productData.product_category || !productData.artisan_name || drivers.length === 0}
                  data-testid="continue-to-drivers"
                >
                  Continue to Certification Questions
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </Button>
              </div>
            </Card>
          )}

          {/* Step 2: Driver Questions */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-driver-questions">
              <div className="flex justify-between items-center">
                <Button variant="ghost" onClick={() => setStep(1)} className="gap-2">
                  <ArrowLeft className="w-4 h-4" /> Back
                </Button>
                <Badge variant="secondary">
                  {answeredDrivers} / {totalDrivers} answered
                </Badge>
              </div>

              {/* Template Info */}
              {selectedTemplate && (
                <Card className="p-4 bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      {(() => {
                        const Icon = TEMPLATE_ICONS[productData.industry_template] || Globe;
                        return <Icon weight="duotone" className="w-5 h-5 text-primary" />;
                      })()}
                    </div>
                    <div>
                      <p className="font-medium">{selectedTemplate.template_name}</p>
                      <p className="text-xs text-muted-foreground">{selectedTemplate.total_drivers} certification drivers</p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Live Score Preview */}
              {previewScore && (
                <Card className="p-6 bg-primary text-primary-foreground">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Live GinsGood Score Preview</p>
                      <div className="flex items-center gap-3">
                        <p className="text-4xl font-bold">{previewScore.ginsgood_score}%</p>
                        <Badge variant="secondary" className="text-xs">
                          {getTierInfo(previewScore.ginsgood_score).icon} {getTierInfo(previewScore.ginsgood_score).name}
                        </Badge>
                      </div>
                      {previewScore.score_cap_applied && (
                        <p className="text-xs opacity-70">* Maximum score is 90%</p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {Object.entries(previewScore.category_percentages || {}).map(([cat, score]) => (
                        <div key={cat} className="text-right">
                          <p className="opacity-70 capitalize">{cat.replace('_', ' ')}</p>
                          <p className="font-semibold">{score}%</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* Driver Questions by Category */}
              {['authenticity', 'heritage', 'social_impact', 'sustainability'].map(category => {
                const categoryDrivers = drivers.filter(d => d.category === category);
                if (categoryDrivers.length === 0) return null;

                return (
                  <Card key={category} className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${getCategoryColor(category)}15`, color: getCategoryColor(category) }}
                      >
                        {getCategoryIcon(category)}
                      </div>
                      <h3 className="text-lg font-medium capitalize">{category.replace('_', ' ')}</h3>
                    </div>

                    <div className="space-y-6">
                      {categoryDrivers.map(driver => (
                        <div key={driver.id}>
                          <Label className="text-base mb-3 block">{driver.question}</Label>
                          <Select 
                            value={driverResponses[driver.id] || ''} 
                            onValueChange={(val) => handleDriverChange(driver.id, val)}
                          >
                            <SelectTrigger data-testid={`driver-${driver.id}`}>
                              <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent>
                              {driver.options.map(option => (
                                <SelectItem key={option} value={option}>{option}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </Card>
                );
              })}

              <Button 
                onClick={() => setStep(3)} 
                className="w-full h-12 rounded-full gap-2"
                disabled={answeredDrivers < totalDrivers}
                data-testid="continue-to-review"
              >
                Review & Submit
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Button>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-review">
              <Button variant="ghost" onClick={() => setStep(2)} className="gap-2">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>

              <Card className="p-6">
                <h3 className="text-xl font-medium mb-6">Review Your Submission</h3>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-medium mb-4">Product Details</h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Name</dt>
                        <dd className="font-medium">{productData.product_name}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Category</dt>
                        <dd>{productData.product_category}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Industry</dt>
                        <dd className="capitalize">{productData.industry_template.replace('_', ' ')}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Artisan</dt>
                        <dd>{productData.artisan_name}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Location</dt>
                        <dd>{productData.artisan_location}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Material</dt>
                        <dd>{productData.material}</dd>
                      </div>
                    </dl>
                  </div>

                  {previewScore && (
                    <div className="bg-secondary/50 rounded-xl p-6 text-center">
                      <p className="text-sm text-muted-foreground mb-2">Estimated GinsGood Score</p>
                      <p className="text-5xl font-bold text-[#4A6C4C]">{previewScore.ginsgood_score}%</p>
                      <Badge className="mt-2" style={{ backgroundColor: getTierInfo(previewScore.ginsgood_score).color }}>
                        {getTierInfo(previewScore.ginsgood_score).icon} {getTierInfo(previewScore.ginsgood_score).name} Tier
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-2">
                        Based on {answeredDrivers} driver responses
                      </p>
                    </div>
                  )}
                </div>
              </Card>

              <Button 
                onClick={handleSubmitCertification} 
                className="w-full h-14 rounded-full text-lg gap-2"
                disabled={loading}
                data-testid="submit-certification-btn"
              >
                {loading ? 'Creating Certificate...' : 'Generate Certificate'}
                <SealCheck weight="bold" className="w-6 h-6" />
              </Button>
            </div>
          )}

          {/* Step 4: Certificate Generated */}
          {step === 4 && certificate && (
            <div className="space-y-6" data-testid="step-certificate">
              <Card className="p-8 text-center">
                <div className="w-24 h-24 mx-auto bg-[#4A6C4C]/10 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle weight="duotone" className="w-12 h-12 text-[#4A6C4C]" />
                </div>
                <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Certificate Generated!
                </h2>
                <p className="text-muted-foreground mb-6">
                  Your product has been certified and verified on blockchain
                </p>

                <div className="bg-secondary/50 rounded-xl p-6 mb-6 text-left max-w-md mx-auto">
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Certificate Number</span>
                      <span className="font-mono font-medium">{certificate.certificate_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">GinsGood Score</span>
                      <span className="font-bold text-[#4A6C4C]">{certificate.ginsgood_score}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tier</span>
                      <Badge style={{ backgroundColor: certificate.certification_tier?.color }}>
                        {certificate.certification_tier?.icon} {certificate.certification_tier?.name}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Blockchain Hash</span>
                      <span className="font-mono text-xs">{certificate.blockchain_hash?.slice(0, 16)}...</span>
                    </div>
                  </div>
                </div>

                {/* QR Code */}
                {certificate.qr_code_base64 && (
                  <div className="mb-6">
                    <p className="text-sm text-muted-foreground mb-2">Scan to verify</p>
                    <img 
                      src={`data:image/png;base64,${certificate.qr_code_base64}`}
                      alt="Certificate QR Code"
                      className="w-40 h-40 mx-auto"
                    />
                  </div>
                )}

                {/* GSG Rewards */}
                {certificate.gsg_rewards && (
                  <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6 mb-6 border border-yellow-200">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <span className="text-2xl">🪙</span>
                      <p className="text-lg font-bold text-yellow-800">
                        You earned {certificate.gsg_rewards.total_earned} GinsGoodCoin!
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                      {certificate.gsg_rewards.rewards?.map((reward, idx) => (
                        <Badge key={idx} className="bg-yellow-100 text-yellow-800 border-yellow-300">
                          +{reward.amount_earned} GSG - {reward.description}
                        </Badge>
                      ))}
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 border-yellow-300 text-yellow-800 hover:bg-yellow-100"
                      onClick={() => navigate('/wallet')}
                    >
                      View Wallet
                    </Button>
                  </div>
                )}

                <div className="flex flex-col gap-3 max-w-sm mx-auto">
                  <Button onClick={() => navigate(`/passport/${certificate.certificate_id}`)} className="w-full gap-2" data-testid="view-passport-btn">
                    View Full Passport
                  </Button>
                  <Button variant="outline" onClick={() => { setStep(1); setCertificate(null); setDriverResponses({}); setPreviewScore(null); }} className="w-full">
                    Certify Another Product
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CertifyPage;
